import { ApiError, get } from "../api.js";
import { dayKey, nextDay } from "../format.js";

export interface PaymentDueLesson {
  appointment_id: number;
  booking_id: number;
  portal_student_id: string | null;
  student_name: string;
  service_name: string;
  starts_at: string;
  state: "due" | "credit_available" | "needs_check";
  amount_due: string | null;
  reason: string;
}

export interface PaymentsDue {
  start_date: string;
  end_date: string;
  evaluated_at: string;
  provider_id: number;
  settled_lessons: number;
  items: PaymentDueLesson[];
}

/** Calendar days in London, so a BST change never adds or removes a day. */
export function paymentsDueRange(days: 1 | 7, now = new Date()) {
  const start = dayKey(now.toISOString());
  let end = start;
  for (let day = 1; day < days; day += 1) end = nextDay(end);
  return { start, end };
}

export async function getPaymentsDue(start: string, end: string): Promise<PaymentsDue | null> {
  let report: PaymentsDue;
  try {
    report = await get<PaymentsDue>("/staff-api/api/v1/reports/payments-due", {
      start_date: start, end_date: end, scope: "mine",
    });
  } catch (cause) {
    // Older bootstraps can omit the provider link, and it may change after
    // sign-in. This specific refusal is inapplicable, not a retryable outage.
    if (cause instanceof ApiError && cause.status === 403 &&
      cause.message === "A linked personal teaching account is required") return null;
    throw cause;
  }
  // A refused/old/malformed API must never turn into an affirmative zero.
  if (report.start_date !== start || report.end_date !== end || !Array.isArray(report.items) ||
    !Number.isSafeInteger(report.provider_id) || report.provider_id <= 0 ||
    report.items.some((item) => !["due", "credit_available", "needs_check"].includes(item.state))) {
    throw new Error("The payment and credit checks could not be confirmed.");
  }
  return report;
}

export function paymentDueStudentCount(items: readonly PaymentDueLesson[]): number {
  return new Set(items.filter((item) => item.state === "due").map((item) => item.portal_student_id ?? `booking:${item.booking_id}`)).size;
}
